(function internalProvider(global) {
  'use strict';

  global.NewHomeSource = global.NewHomeSource || {};
  global.NewHomeSource.videoPlayers = global.NewHomeSource.videoPlayers || {};
  global.NewHomeSource.videoPlayers.internalProvider = global.NewHomeSource.videoPlayers.internalProvider || {};

  const milestoneEventParameters = {
    75: { googleAnalytics4EventName: 'video_75%' },
    50: { googleAnalytics4EventName: 'video_50%' },
    25: { googleAnalytics4EventName: 'video_25%' },
  };

  /**
     * @private
     * triggerOnDemandTracker: dispatch the event trackOnDemandEvent
     *
     * @param {Object} eventInformation object with the event information to be triggered.
     */
  function triggerOnDemandTracker(eventInformation) {
    document.body.dispatchEvent(new CustomEvent('trackOnDemandEvent', {
      detail: {
        data: eventInformation
      }
    }));
  }

  /**
     * @private
     * triggerEvents: triggers the events with the parameter values.
     *
     * @param {Object} eventParameters object parameter values that will be used on the events.
     */
  function triggerEvents(eventParameters) {
    const { googleAnalytics4EventName } = eventParameters;
    const events = [];
    if (googleAnalytics4EventName) {
      const googleAnalytics4Event = {
        eventType: 'GA4Event',
        eventName: googleAnalytics4EventName,
        params: {
          video_type: 'Internal',
          click_target: 'Video event'
        }
      };
      events.push(googleAnalytics4Event);
    }

    events.forEach(triggerOnDemandTracker);
  }

  const defaultEvents = {
    onEnded: function onEnded() {
      triggerEvents({
        googleAnalytics4EventName: 'video_completed'
      });
    },
    onPlaying: function onPlaying() {},
    onPaused: function onPaused() {
      triggerEvents({
        googleAnalytics4EventName: 'video_pause'
      });
    },
    onLoaded: function onLoaded() {},
    onMilestone: function onMilestone(data) {
      const eventParameters = milestoneEventParameters[data && data.percent];
      if (eventParameters) {
        triggerEvents(eventParameters);
      }
    },
  };

  const scriptUrlInput = document.querySelector('[data-zonda-video-player-src]');

  let playerElement;
  let thumbnail;
  let eventsHandler;
  let abortController;
  let source;

  /**
     * @private
     * playingHandler: triggers the onPlaying event handler.
     *
     * @param {Object} data event data.
     */
  function playingHandler(data) {
    const eventData = data;
    eventData.videoId = source;
    eventsHandler.onPlaying(eventData);
  }

  /**
     * @private
     * resolveElement: returns an element from an ID string or passes the element through.
     *
     * @param {string|HTMLElement} video
     * @returns {HTMLElement}
     */
  function resolveElement(video) {
    return typeof video === 'string' ? document.getElementById(video) : video;
  }

  /**
     * @private
     * wireEvents: attaches vp:* listeners to the current playerElement, replacing any prior listeners.
     * Uses AbortController so all listeners are removed in a single abort() call.
     */
  function wireEvents() {
    if (abortController) {
      abortController.abort();
    }

    abortController = new AbortController();
    const { signal } = abortController;

    playerElement.addEventListener('vp:play', (event) => {
      playingHandler(event);
    }, { signal });

    playerElement.addEventListener('vp:pause', () => {
      eventsHandler.onPaused({});
    }, { signal });

    playerElement.addEventListener('vp:ended', () => {
      eventsHandler.onEnded({});
    }, { signal });

    playerElement.addEventListener('vp:milestone', (event) => {
      eventsHandler.onMilestone(event.detail);
    }, { signal });
  }

  /**
     * @private
     * configurePlayer: sets attributes and wires events once zonda-video-player is available.
     * Called from loadZondaVideoPlayer().then(...).
     *
     * @param {object} config - optional callbacks and options.
     */
  function configurePlayer() {
    playerElement.setAttribute('video-url', source);

    if (thumbnail) {
      playerElement.setAttribute('thumbnail-url', thumbnail);
    }

    wireEvents();

    if (eventsHandler.playOnLoaded) {
      playerElement.play();
    }
  }

  /**
     * @private
     * loadZondaVideoPlayer: ensures the <zonda-video-player> custom element is defined
     * before configuring the player. Resolution order:
     *   1. Fast path -- the custom element is already registered (a prior Player() call
     *      already loaded it), so resolve immediately.
     *   2. RequireJS (global.define) -- the path this module is always loaded through today,
     *      since internalProvider.js is only ever pulled in as the 'internalProviderVideoPlayer'
     *      AMD dependency of builderShowcaseVideoHandler.js. scriptUrlInput.value (the full
     *      hashed URL, see below) is passed straight to require() as the module id, bypassing
     *      RequireJS's baseUrl/paths resolution (see jsExtRegExp in require.js) so the r.js
     *      optimizer never has to trace/parse this ES2020+ third-party file at build time.
     *   3. Plain <script> tag -- matches the fallback used by the sibling brightcove/vimeo/
     *      youtube players: a <script> is created manually and inserted before the page's
     *      first <meta> tag, resolving on 'load'. Not reachable today since internalProvider.js
     *      is only ever loaded through RequireJS (global.define is always truthy by the time
     *      this code runs), but kept for parity with the other players in case this module is
     *      ever loaded on a non-RequireJS page.
     *   Rejects immediately if the hidden input matched by scriptUrlInput is missing or has no
     *   value, since none of the above can proceed without a script URL.
     *
     * @returns {Promise<void>}
     */
  function getValidatedScriptUrl(rawUrl) {
    if (!rawUrl) {
      return null;
    }

    try {
      const parsed = new URL(rawUrl, global.location.href);
      const isHttpProtocol = parsed.protocol === 'https:' || parsed.protocol === 'http:';

      if (!isHttpProtocol) {
        return null;
      }

      return parsed.href;
    } catch (e) {
      return null;
    }
  }

  function loadZondaVideoPlayer() {
    return new Promise((resolve, reject) => {
      if (global.customElements && global.customElements.get('zonda-video-player')) {
        resolve();
        return;
      }

      if (!scriptUrlInput || !scriptUrlInput.value) {
        reject();
        return;
      }

      const validatedScriptUrl = getValidatedScriptUrl(scriptUrlInput.value);
      if (!validatedScriptUrl) {
        reject();
        return;
      }

      if (global.define) {
        require(
          [validatedScriptUrl],
          () => { resolve(); }
        );
        return;
      }

      const script = document.createElement('script');
      script.async = true;
      script.defer = true;
      script.src = validatedScriptUrl;
      script.type = 'text/javascript';
      script.onload = resolve;
      script.onerror = reject;
      document.body.appendChild(script);
    });
  }

  /**
   * @constructs InternalProviderPlayer
   *
   * Creates a new video player instance for internal video playback.
   *
   * @param {Object} config - Configuration object containing custom event handlers and video details.
   * @param {string} config.elementId - The ID of the DOM element where the player will be rendered.
   * @param {string} config.videoUrl - The URL of the video to be played.
   * @param {string} config.thumbnailUrl - The URL of the video thumbnail image.
   * @param {object} config.eventsHandler - The events to attached for the player.
   */
  function Player(config) {
    source = config.videoUrl;
    playerElement = resolveElement(config.elementId);
    thumbnail = config.thumbnailUrl;
    eventsHandler = Object.assign({}, defaultEvents, config.eventsHandler);
    loadZondaVideoPlayer().then(configurePlayer);
  }

  /**
     * @public
     * createContainer: creates a bare <zonda-video-player> custom element ready to be inserted
     * into the DOM. Video URL and thumbnail are configured by the Player constructor.
     *
     * @returns {HTMLElement}
     */
  Player.createContainer = function createContainer() {
    const videoElement = document.createElement('zonda-video-player');
    videoElement.classList.add('nhs-video-player__container--internal');
    videoElement.id = 'videoContainer';
    videoElement.setAttribute('layout', 'fill-container');
    videoElement.setAttribute('lang', 'es');
    videoElement.style.width = '100%';
    videoElement.style.height = '100%';
    return videoElement;
  };

  /**
     * @public
     * setVideo: loads a new source into the player, re-targeting the element if needed.
     *
     * @param {string|HTMLElement} video - element ID or the <zonda-video-player> element.
     * @param {string} newSource - new HLS or MP4 URL.
     */
  Player.setVideo = function setVideo(video, newSource) {
    if (video) {
      playerElement = resolveElement(video);
    }

    if (!playerElement || !newSource) {
      return;
    }

    source = newSource;
    playerElement.setAttribute('video-url', source);
  };

  /**
     * @public
     * dispose: removes all event listeners and destroys the underlying player.
     */
  Player.dispose = function dispose() {
    try {
      if (abortController) {
        abortController.abort();
        abortController = null;
      }

      if (playerElement) {
        playerElement.destroy();
        playerElement = null;
      }
    } catch (error) {
      console.error(error);
      // swallow — element may already be destroyed or removed from DOM
    }
  };

  /**
     * @public
     * pause: pauses playback.
     */
  Player.pause = function pause() {
    try {
      if (playerElement) {
        playerElement.pause();
      }
    } catch (error) {
      console.error(error);
      // swallow — player may not be initialised yet
    }
  };

  /**
     * @public
     * play: resumes or starts playback.
     */
  Player.play = function play() {
    try {
      if (playerElement) {
        playerElement.play();
      }
    } catch (error) {
      console.error(error);
      // swallow — player may not be initialised yet
    }
  };

  global.NewHomeSource.videoPlayers.internalProvider.Player = Player;
}(window));
